
import { Component, signal, output, input, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, AbstractControl } from '@angular/forms';

interface DropdownOption {
  value: string | number;
  viewValue: string;
  disabled?: boolean;
}

@Component({
  standalone: true,
  selector: 'base-dropdown-mobile-filter',
  templateUrl: './dropdown-mobile-filter.component.html',
  styleUrls: ['./dropdown-mobile-filter.component.scss'],
  imports: [CommonModule, ReactiveFormsModule]
})
export class DropdownMobileFilterComponent {

  private _control = signal<FormControl | AbstractControl | null>(null);
  private _message = signal<string>('');
  private _placeholder = signal<string>('');
  private _multiple = signal<boolean>(false);
  private _value = signal<string>('');
  private _required = signal<boolean>(false);
  private _label = signal<string>('');
  private _list = signal<DropdownOption[]>([]);
  private _errorMessages = signal<{ [key: string]: string }>({});
  private _focused = signal<boolean>(false);
  private _width = signal<string | number>('auto');
  private _defaultOption = signal<string>('');



  readonly controlInput = input<FormControl | AbstractControl | null>(null);
  readonly messageInput = input<string>('');
  readonly placeholderInput = input<string>('');
  readonly multipleInput = input<boolean>(false);
  readonly valueInput = input<string>('');
  readonly requiredInput = input<boolean>(false);
  readonly labelInput = input<string>('');
  readonly listInput = input<DropdownOption[]>([]);
  readonly errorMessagesInput = input<{ [key: string]: string }>({});
  readonly focusedInput = input<boolean>(false);
  readonly widthInput = input<string | number>('auto');
  readonly defaultOptionInput = input<string>('');

  // Getters for backward compatibility
  get control() { return this._control(); }
  get message() { return this._message(); }
  get placeholder() { return this._placeholder(); }
  get multiple() { return this._multiple(); }
  get value() { return this._value(); }
  get required() { return this._required(); }
  get label() { return this._label(); }
  get list() { return this._list(); }
  get errorMessages() { return this._errorMessages(); }
  get focused() { return this._focused(); }
  get width() { return this._width(); }
  get defaultOption() { return this._defaultOption(); }

  // Update outputs to use Angular 20 output() function
  valueChanged = output<string | number>();
  openedChange = output<boolean>();

  constructor() {
    effect(() => {
      const controlValue = this.controlInput();
      this._control.set(controlValue);
    });

    effect(() => {
      const messageValue = this.messageInput();
      this._message.set(messageValue);
    });

    effect(() => {
      const placeholderValue = this.placeholderInput();
      this._placeholder.set(placeholderValue);
    });

    effect(() => {
      const multipleValue = this.multipleInput();
      this._multiple.set(multipleValue);
    });

    effect(() => {
      const valueValue = this.valueInput();
      this._value.set(valueValue);
    });

    effect(() => {
      const requiredValue = this.requiredInput();
      this._required.set(requiredValue);
    });

    effect(() => {
      const labelValue = this.labelInput();
      this._label.set(labelValue);
    });

    effect(() => {
      const listValue = this.listInput();
      this._list.set(listValue);
    });

    effect(() => {
      const errorMessagesValue = this.errorMessagesInput();
      this._errorMessages.set(errorMessagesValue);
    });

    effect(() => {
      const focusedValue = this.focusedInput();
      this._focused.set(focusedValue);
    });

    effect(() => {
      const widthValue = this.widthInput();
      this._width.set(widthValue);
    });

    effect(() => {
      const defaultOptionValue = this.defaultOptionInput();
      this._defaultOption.set(defaultOptionValue);
    });
  }

  readonly formControl = computed(() => this._control() as FormControl);

  readonly errorMessage = computed(() => {
    const control = this._control();
    const errorMessages = this._errorMessages();

    if (control?.errors && errorMessages) {
      const errorKeys = Object.keys(control.errors);
      if (errorKeys.length > 0) {
        const firstErrorKey = errorKeys[0]!;
        const errorMessage = errorMessages[firstErrorKey];
        if (errorMessage) {
          return errorMessage;
        }
      }
    }
    return '';
  });

}


